import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useIsSignedIn, useTransport } from "@empite/session";
import {
  createMarketplaceClient,
} from "./marketplace-client";
import type {
  Comment,
  CommentBody,
  Counters,
  Creation,
  CreateCreationBody,
  CreationFile,
  DownloadTicket,
  MarketplaceClient,
  PatchCreationBody,
  ReadMount,
  Report,
  ReportBody,
  UploadRequestBody,
} from "./marketplace-client";
import { putBytes, sha256Hex } from "./upload";

/**
 * The marketplace query keys.
 *
 * Every key carries the MOUNT it was read from. That is not decoration: the
 * public and member mounts answer the same question differently — a stranger
 * gets a 404 for a draft its author can open, and `liked` is always false
 * with nobody signed in. Sharing one cache entry between the two would show
 * the answer from before the sign-in.
 */
export const marketplaceKeys = {
  all: ["marketplace"] as const,
  browse: (mount: ReadMount, q: string) => ["marketplace", mount, "browse", q] as const,
  creation: (mount: ReadMount, id: string) => ["marketplace", mount, "creation", id] as const,
  counters: (mount: ReadMount, id: string) => ["marketplace", mount, "creation", id, "counters"] as const,
  comments: (mount: ReadMount, id: string) => ["marketplace", mount, "creation", id, "comments"] as const,
  mine: ["marketplace", "member", "mine"] as const,
  reasons: (mount: ReadMount) => ["marketplace", mount, "report-reasons"] as const,
};

/** How many rows a list asks for. A marketplace is browsed, not exported. */
export const PAGE_SIZE = 24;

export function useMarketplace(): MarketplaceClient {
  const transport = useTransport();
  return useMemo(() => createMarketplaceClient(transport), [transport]);
}

export interface Viewer {
  mount: ReadMount;
  /** False until the session has been resolved once. Reads wait for it so an
   *  author opening their own draft is not shown a 404 from the anonymous
   *  mount in the moment before the cookie is read. */
  ready: boolean;
  signedIn: boolean;
  userID: string | null;
}

export function useViewer(): Viewer {
  const { ready, signedIn, session } = useIsSignedIn();
  return {
    mount: signedIn ? "member" : "public",
    ready,
    signedIn,
    userID: session?.user_id ?? null,
  };
}

export function useBrowse(query: string): UseQueryResult<Creation[]> {
  const client = useMarketplace();
  const viewer = useViewer();
  return useQuery({
    queryKey: marketplaceKeys.browse(viewer.mount, query),
    queryFn: () => client.browse(viewer.mount, { q: query, limit: PAGE_SIZE }),
    enabled: viewer.ready,
  });
}

export function useCreation(creationID: string): UseQueryResult<Creation> {
  const client = useMarketplace();
  const viewer = useViewer();
  return useQuery({
    queryKey: marketplaceKeys.creation(viewer.mount, creationID),
    queryFn: () => client.getCreation(viewer.mount, creationID),
    enabled: viewer.ready,
  });
}

export function useCounters(creationID: string): UseQueryResult<Counters> {
  const client = useMarketplace();
  const viewer = useViewer();
  return useQuery({
    queryKey: marketplaceKeys.counters(viewer.mount, creationID),
    queryFn: () => client.counters(viewer.mount, creationID),
    enabled: viewer.ready,
  });
}

export function useComments(creationID: string): UseQueryResult<Comment[]> {
  const client = useMarketplace();
  const viewer = useViewer();
  return useQuery({
    queryKey: marketplaceKeys.comments(viewer.mount, creationID),
    queryFn: () => client.listComments(viewer.mount, creationID, { limit: PAGE_SIZE * 4 }),
    enabled: viewer.ready,
  });
}

export function useMyCreations(): UseQueryResult<Creation[]> {
  const client = useMarketplace();
  const viewer = useViewer();
  return useQuery({
    queryKey: marketplaceKeys.mine,
    queryFn: () => client.listMine({ limit: PAGE_SIZE * 2 }),
    enabled: viewer.signedIn,
  });
}

export function useReportReasons(): UseQueryResult<string[]> {
  const client = useMarketplace();
  const viewer = useViewer();
  return useQuery({
    queryKey: marketplaceKeys.reasons(viewer.mount),
    queryFn: () => client.reportReasons(viewer.mount),
    enabled: viewer.ready,
    // A closed vocabulary, and one the server owns. Refetching it per screen
    // would be a request per navigation for a list that changes on a deploy.
    staleTime: 10 * 60_000,
  });
}

/**
 * Every mutation invalidates the WHOLE marketplace subtree.
 *
 * The counters are derived from event tables, so a like changes a creation's
 * totals, the browse row that shows them and the detail page at once — and
 * both mounts hold a copy. Invalidating precisely would mean re-deriving the
 * server's fan-out in the client, and getting it wrong leaves a like on
 * screen that the database never recorded.
 */
function useInvalidateAll(): () => Promise<void> {
  const queries = useQueryClient();
  return () => queries.invalidateQueries({ queryKey: marketplaceKeys.all });
}

function useMarketplaceMutation<Input, Output>(
  fn: (client: MarketplaceClient, input: Input) => Promise<Output>,
): UseMutationResult<Output, unknown, Input> {
  const client = useMarketplace();
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: Input) => fn(client, input),
    onSuccess: invalidate,
  });
}

export function useCreateCreation(): UseMutationResult<Creation, unknown, CreateCreationBody> {
  return useMarketplaceMutation((client, body) => client.createCreation(body));
}

export function useUpdateCreation(
  creationID: string,
): UseMutationResult<Creation, unknown, PatchCreationBody> {
  return useMarketplaceMutation((client, body) => client.updateCreation(creationID, body));
}

export function useDeleteCreation(): UseMutationResult<void, unknown, string> {
  return useMarketplaceMutation((client, creationID) => client.deleteCreation(creationID));
}

export interface PublishInput {
  creationID: string;
  publish: boolean;
}

/** One hook, two ROUTES. "publish" must never be what a malformed request
 *  falls back to, which is why the server has no single status endpoint. */
export function useSetPublished(): UseMutationResult<Creation, unknown, PublishInput> {
  return useMarketplaceMutation((client, { creationID, publish }) =>
    publish ? client.publish(creationID) : client.unpublish(creationID),
  );
}

export interface LikeInput {
  creationID: string;
  liked: boolean;
}

/**
 * useSetLike sends the STATE the reader wants, not a toggle.
 *
 * Both directions are idempotent server-side — the like table is PK'd on
 * (creation_id, user_id) — so a double tap, a retry after a dropped response
 * and a replayed request all converge on the same single like.
 */
export function useSetLike(): UseMutationResult<Counters, unknown, LikeInput> {
  return useMarketplaceMutation((client, { creationID, liked }) => client.setLike(creationID, liked));
}

export interface AddCommentInput {
  creationID: string;
  body: CommentBody;
}

export function useAddComment(): UseMutationResult<Comment, unknown, AddCommentInput> {
  return useMarketplaceMutation((client, { creationID, body }) => client.addComment(creationID, body));
}

export interface EditCommentInput {
  commentID: string;
  body: string;
}

export function useEditComment(): UseMutationResult<Comment, unknown, EditCommentInput> {
  return useMarketplaceMutation((client, { commentID, body }) => client.editComment(commentID, body));
}

export function useDeleteComment(): UseMutationResult<void, unknown, string> {
  return useMarketplaceMutation((client, commentID) => client.deleteComment(commentID));
}

export interface ReportInput {
  subject: { creationID: string } | { commentID: string };
  body: ReportBody;
}

export function useReport(): UseMutationResult<Report, unknown, ReportInput> {
  return useMarketplaceMutation((client, { subject, body }) =>
    "creationID" in subject
      ? client.reportCreation(subject.creationID, body)
      : client.reportComment(subject.commentID, body),
  );
}

/**
 * useDownload turns a file into bytes on disk.
 *
 * Two steps, never one: the API AUTHORISES first — refusing an unpublished or
 * quarantined file, and recording who asked — and only then hands back a
 * short-lived grant the browser spends immediately. It is a mutation rather
 * than a query because it is a recorded event, and a cached download is a
 * download that did not happen.
 */
export function useDownload(): UseMutationResult<DownloadTicket, unknown, { creationID: string; fileID: string }> {
  return useMarketplaceMutation((client, { creationID, fileID }) =>
    client.authorizeDownload(creationID, fileID),
  );
}

/** How far an upload has got, so the wizard can say so. */
export type UploadPhase = "hashing" | "granting" | "sending" | "completing";

export interface UploadInput {
  creationID: string;
  file: File;
  onPhase?: (phase: UploadPhase) => void;
}

/**
 * useUploadFile drives the whole lifecycle for one file.
 *
 * hash → grant → PUT to the store → complete. The order is the server's, not
 * a preference: the checksum is DECLARED when the grant is issued, so it has
 * to be computed before the URL exists, and completion is what compares it to
 * the bytes that actually landed. The value it resolves to is the file after
 * its scan verdict — active, rejected or quarantined — because that, and not
 * a 200 from the store, is what says the upload worked.
 */
export function useUploadFile(): UseMutationResult<CreationFile, unknown, UploadInput> {
  return useMarketplaceMutation(async (client, { creationID, file, onPhase }) => {
    onPhase?.("hashing");
    const checksum = await sha256Hex(file);

    onPhase?.("granting");
    const body: UploadRequestBody = {
      filename: file.name,
      // Recorded and then never consulted again — the server does not
      // dispatch on it, and neither does this portal. A browser that offers
      // nothing gets the generic type rather than a failed upload.
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      checksum_sha256: checksum,
    };
    const ticket = await client.requestUpload(creationID, body);

    onPhase?.("sending");
    await putBytes(ticket.upload, file);

    onPhase?.("completing");
    return client.completeUpload(creationID, ticket.file.id);
  });
}

export interface ModerateCommentInput {
  commentID: string;
  action: "hide" | "restore" | "remove";
  body: ReportBody;
}

/**
 * useModerateComment is the CREATION author acting on a comment under their
 * own work — the same verbs an operator uses in the back office, recorded
 * against actor_user_id instead of admin_user_id so the two authorities can
 * never be read back as one. The reason is mandatory, as it is for every
 * moderation decision in this platform.
 */
export function useModerateComment(): UseMutationResult<Comment, unknown, ModerateCommentInput> {
  return useMarketplaceMutation((client, { commentID, action, body }) =>
    client.moderateComment(commentID, action, body),
  );
}
